import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Stack, Chip, Divider, Table, TableHead, TableRow,
  TableCell, TableBody, Button, IconButton, CircularProgress, Alert, Tooltip, TableContainer,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PersonIcon from '@mui/icons-material/Person';
import {
  collection, doc, getDoc, getDocs, query, where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useBranch } from '../../context/BranchContext';
import { generateBoletaPDF } from '../../services/boletaPDF';
import { CLP, FINANCE_COLORS, fmtFecha, labelMedio } from '../../utils/finance';

const { PRIMARY, SUCCESS, AMBER } = FINANCE_COLORS;

export default function CuentaPaciente() {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const { activeBranch, filterByBranch } = useBranch();
  const [owner, setOwner] = useState(null);
  const [patients, setPatients] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [chargesByPatient, setChargesByPatient] = useState({});
  const [hospitalizaciones, setHospitalizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const oSnap = await getDoc(doc(db, 'owners', ownerId));
        if (oSnap.exists()) setOwner({ id: oSnap.id, ...oSnap.data() });

        const pSnap = await getDocs(query(collection(db, 'patients'), where('ownerId', '==', ownerId)));
        const plist = filterByBranch(pSnap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true });
        setPatients(plist);

        const vSnap = await getDocs(query(collection(db, 'Sales'), where('ownerId', '==', ownerId)));
        setVentas(filterByBranch(vSnap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true })
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));

        // Cargos pendientes + hospitalizaciones por paciente
        const chMap = {};
        const hosp = [];
        for (const p of plist) {
          const chSnap = await getDocs(query(
            collection(db, 'patient_accounts', p.id, 'charges'), where('status', '==', 'pending')));
          chMap[p.id] = filterByBranch(chSnap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true });
          const hSnap = await getDocs(query(collection(db, 'hospitalizations'), where('patientId', '==', p.id)));
          hSnap.forEach((d) => hosp.push({ id: d.id, patientName: p.name, ...d.data() }));
        }
        setChargesByPatient(chMap);
        setHospitalizaciones(filterByBranch(hosp, { includeUnassigned: true }));
      } catch (e) {
        setError('Error al cargar la cuenta: ' + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [ownerId]);

  const totalVentas = useMemo(
    () => ventas.filter((v) => v.estado !== 'anulada').reduce((s, v) => s + (v.totalFinal || v.total || 0), 0),
    [ventas]);
  const totalPendiente = useMemo(
    () => Object.values(chargesByPatient).flat().reduce((s, c) => s + (Number(c.totalAmount) || 0), 0),
    [chargesByPatient]);

  const reimprimir = async (v) => {
    await generateBoletaPDF(v, { name: v.ownerName, rut: v.ownerRut }, { name: v.patientName }, { branch: activeBranch });
  };

  if (loading) return <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ pb: 6 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
        <PersonIcon sx={{ color: PRIMARY }} />
        <Typography variant="h5" fontWeight={800} color={PRIMARY}>Estado de Cuenta</Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Header owner */}
      <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" fontWeight={800}>{owner?.name || '–'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {owner?.rut ? `RUT: ${owner.rut}` : ''} {owner?.celular ? ` · ${owner.celular}` : ''} {owner?.mail || owner?.email ? ` · ${owner.mail || owner.email}` : ''}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
              {patients.map((p) => <Chip key={p.id} label={`${p.name} (${p.species || '–'})`} size="small" />)}
            </Stack>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Total facturado</Typography>
            <Typography variant="h6" fontWeight={800} color={SUCCESS}>{CLP(totalVentas)}</Typography>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Typography variant="caption" color="text.secondary">Pendiente por cobrar</Typography>
            <Typography variant="h6" fontWeight={800} color={AMBER}>{CLP(totalPendiente)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        {/* Historial de ventas */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ p: 2 }}>Historial de ventas</Typography>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Boleta</TableCell>
                    <TableCell>Medio</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ventas.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin ventas.</TableCell></TableRow>
                  ) : ventas.map((v) => (
                    <TableRow key={v.id} hover sx={{ opacity: v.estado === 'anulada' ? 0.5 : 1 }}>
                      <TableCell>{fmtFecha(v.createdAt)}</TableCell>
                      <TableCell>{v.folioDocumento || v.boletaNumber || '–'}</TableCell>
                      <TableCell>{(v.pagos || [{ medio: v.paymentMethod }]).map((p) => labelMedio(p.medio)).join(', ')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{CLP(v.totalFinal || v.total)}</TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => reimprimir(v)}><ReceiptIcon fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Cargos pendientes por paciente */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Cargos pendientes</Typography>
            {patients.map((p) => {
              const charges = chargesByPatient[p.id] || [];
              return (
                <Box key={p.id} sx={{ mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" fontWeight={700}>{p.name}</Typography>
                    {charges.length > 0 && (
                      <Button size="small" startIcon={<PointOfSaleIcon />} onClick={() => navigate(`/caja/ticket?patientId=${p.id}`)}>
                        Cobrar
                      </Button>
                    )}
                  </Stack>
                  {charges.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">Sin cargos pendientes.</Typography>
                  ) : (
                    <Table size="small">
                      <TableBody>
                        {charges.map((c) => (
                          <TableRow key={c.id}>
                            <TableCell sx={{ border: 0, py: 0.3 }}>{c.description}</TableCell>
                            <TableCell align="right" sx={{ border: 0, py: 0.3 }}>{CLP(c.totalAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              );
            })}
            {patients.length === 0 && <Typography variant="body2" color="text.secondary">Sin pacientes asociados.</Typography>}
          </Paper>

          {/* Hospitalizaciones */}
          {hospitalizaciones.length > 0 && (
            <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, p: 2, mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Hospitalizaciones</Typography>
              {hospitalizaciones.map((h) => (
                <Box key={h.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Box>
                    <Typography variant="body2">{h.patientName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtFecha(h.admissionDate || h.createdAt)} · {h.status || '–'}
                    </Typography>
                  </Box>
                  <Chip size="small" label={h.status || '–'} />
                </Box>
              ))}
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
